import KamailioRoutes from '@/modules/kamailio/routes'
import LinksRoutes from '@/modules/links/routes'
import TicketRoutes from '@/modules/ticket/routes'
import ToolsRoutes from '@/modules/tools/routes'
import TPPRoutes from '@/modules/tpp/routes'
import AdminRoutes from '@/views/admin/routes'
import ArticleRoutes from '@/views/article/routes'
import CompanyRoutes from '@/views/company/routes'
import FinanceRoutes from '@/views/finance/routes'
import ReportRoutes from '@/views/report/routes'
import { sentryUtils } from '@/lib/sentry'
import DatumRoutes from '@/views/sales/datum/routes'
import RateRoutes from '@/views/sales/rate/routes'
import ServiceRoutes from '@/views/sales/services/routes'
import TariffRoutes from '@/views/sales/tariff/routes'
import SystemRoutes from '@/views/system/routes'
import UsageRoutes from '@/views/usage/routes'
import AssetTagRoutes from '@/views/asset-tag/routes'
import ProductRoutes from '@/views/sales/product/routes'
import StaffSettingsRoutes from '@/views/settings/routes'
import VocusMobileRoutes from '@/modules/vocus-mobile/routes'
import CloudflareRoutes from '@/modules/cloudflare/routes'
import RadiusRoutes from '@/modules/radius/routes'
import FormsRoutes from '@/views/forms/routes'
import LogsRoutes from '@/views/logs/routes'

import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useUserQuery } from '@/queries/user'
import { watchEffect } from 'vue'

const routes: Array<RouteRecordRaw> = [
    AssetTagRoutes,
    SystemRoutes,
    TPPRoutes,
    KamailioRoutes,
    UsageRoutes,
    VocusMobileRoutes,
    FormsRoutes,
    RadiusRoutes,
    CloudflareRoutes,
    LogsRoutes,
    {
        path: '/',
        component: () => import('@/views/Dashboard.vue'),
        name: 'dashboard',
        meta: {
            auth: true,
            label: 'Dashboard',
            location: 'sidebar-top',
            icon: 'fal fa-gauge-max fa-fw',
            group: 'main',
            children: true,
        },
        children: [
            {
                path: 'dashboard/technical',
                name: 'dashboard.technical.index',
                component: () => import('@/views/dashboard/technical/Index.vue'),
                meta: {
                    label: 'Technical',
                    auth: true,
                    //permission: 'manage-technical',
                    icon: 'fal fa-drafting-compass fa-fw',
                    location: 'sidebar',
                    parent: 'dashboard',
                },
            },
            {
                path: 'dashboard/finance',
                name: 'dashboard.finance.index',
                component: () => import('@/views/dashboard/finance/Index.vue'),
                meta: {
                    label: 'Finance',
                    auth: true,
                    permission: 'manage-finance',
                    icon: 'fal fa-landmark fa-fw',
                    location: 'sidebar',
                    parent: 'dashboard',
                },
            },
            {
                path: 'dashboard/alerts',
                name: 'dashboard.alerts.index',
                component: () => import('@/views/dashboard/alert/Index.vue'),
                meta: {
                    label: 'Alerts',
                    auth: true,
                    permission: 'manage-alerts',
                    icon: 'fal fa-exclamation-triangle fa-fw',
                    location: 'sidebar',
                    parent: 'dashboard',
                },
            },
        ],
    },
    CompanyRoutes,
    TicketRoutes,
    {
        path: '/sales',
        component: () => import('@/views/Index.vue'),
        name: 'sales',
        meta: {
            label: 'Sales',
            auth: true,
            location: 'sidebar-top',
            icon: 'fal fa-analytics fa-fw',
            permission: 'manage-customers',
            group: 'main',
        },
        children: [
            {
                path: 'orders',
                name: 'sales.orders.index',
                component: () => import('@/views/sales/order/Index.vue'),
                meta: {
                    label: 'Orders',
                    auth: true,
                    permission: 'manage-orders',
                    icon: 'fal fa-shopping-cart fa-fw',
                    location: 'sidebar',
                    parent: 'sales',
                },
                children: [
                    {
                        path: 'create',
                        name: 'sales.orders.create',
                        component: () => import('@/views/sales/order/Index.vue'),
                        meta: {
                            auth: true,
                            label: 'Create Order',
                            permission: 'manage-orders',
                            parent: 'sales.orders.index',
                        },
                    },
                    {
                        path: ':orderId/edit',
                        name: 'sales.orders.edit',
                        component: () => import('@/views/sales/order/Index.vue'),
                        meta: {
                            label: 'Edit Order',
                            auth: true,
                            permission: 'manage-orders',
                            parent: 'sales.orders.index',
                        },
                    },
                    {
                        path: ':orderId/details',
                        name: 'sales.orders.details',
                        component: () => import('@/views/sales/order/Index.vue'),
                        meta: {
                            label: 'Order Details',
                            auth: true,
                            permission: 'manage-orders',
                            parent: 'sales.orders.index',
                        },
                        children: [
                            {
                                path: 'create',
                                name: 'sales.order-items.create',
                                component: () => import('@/views/sales/order/Index.vue'),
                                meta: {
                                    auth: true,
                                    label: 'Create Order Item',
                                    permission: 'manage-orders',
                                    parent: 'sales.orders.details',
                                },
                            },
                            {
                                path: ':orderItemId/edit',
                                name: 'sales.order-items.edit',
                                component: () => import('@/views/sales/order/Index.vue'),
                                meta: {
                                    label: 'Edit Order Item',
                                    auth: true,
                                    permission: 'manage-orders',
                                    parent: 'sales.orders.details',
                                },
                            },
                        ],
                    },
                    {
                        path: ':orderId/notify',
                        name: 'sales.orders.notify',
                        component: () => import('@/views/sales/order/Index.vue'),
                        meta: {
                            label: 'Send Order Notification',
                            auth: true,
                            permission: 'manage-orders',
                            parent: 'sales.orders.index',
                        },
                    },
                ],
            },
            ProductRoutes,
            {
                path: 'packages',
                name: 'sales.packages.index',
                component: () => import('@/views/sales/package/Index.vue'),
                meta: {
                    label: 'Packages',
                    auth: true,
                    permission: 'manage-packages',
                    icon: 'fab fa-dropbox fa-fw',
                    location: 'sidebar',
                    parent: 'sales',
                },
            },
            RateRoutes,
            ServiceRoutes,
            DatumRoutes,
            TariffRoutes,
        ],
    },
    FinanceRoutes,
    ArticleRoutes,
    ReportRoutes,
    LinksRoutes,
    ToolsRoutes,
    {
        path: '/domains',
        component: () => import('@/views/Index.vue'),
        name: 'domains',
        meta: {
            label: 'Domains',
            auth: true,
            location: 'sidebar-top',
            icon: 'fal fa-globe fa-fw',
            permission: 'manage-domains',
            group: 'main',
            children: true,
        },
        children: [
            {
                path: 'expiring',
                name: 'domains.expiring.index',
                component: () => import('@/views/domain/expiring/Index.vue'),
                meta: {
                    label: 'Expiring',
                    auth: true,
                    permission: 'manage-domains',
                    icon: 'fal fa-file-chart-line fa-fw',
                    location: 'sidebar',
                    parent: 'domains',
                },
                children: [
                    {
                        path: ':serviceId?/expiring',
                        name: 'domains.expiring.notify',
                        component: () => import('@/views/domain/expiring/Index.vue'),
                        meta: {
                            label: 'Notify',
                            auth: true,
                            permission: 'manage-domains',
                            icon: 'fal fa-file-chart-line fa-fw',
                            location: 'sidebar',
                            parent: 'domains',
                        },
                    },
                ],
            },
        ],
    },
    AdminRoutes,
    StaffSettingsRoutes,
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/login/Index.vue'),
        meta: {
            guest: true,
            label: 'Login',
        },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/forgot-password/Index.vue'),
        meta: {
            guest: true,
            label: 'Forgot password',
        },
    },
    {
        path: '/password/reset/:token',
        name: 'reset-password',
        component: () => import('@/views/reset-password/Index.vue'),
        meta: {
            guest: true,
            label: 'Reset password',
        },
    },
    {
        path: '/:pathMatch(.*)',
        redirect: { name: 'dashboard' },
        meta: {},
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    strict: true,
})

router.beforeEach(async (to) => {
    const requiresAuth = to.matched.some((record) => record.meta.auth)
    const requiresGuest = to.matched.some((record) => record.meta.guest)

    try {
        const { data: user, isSuccess, error } = useUserQuery({})

        await new Promise((resolve, reject) => {
            watchEffect(() => {
                if (isSuccess.value) {
                    resolve(user.value)
                }

                if (error.value) {
                    reject(error.value)
                }
            })
        })

        const userStore = useUserStore()

        document.title = `${to.meta?.title || to.meta?.label} - Amphora Staff`

        if (!user.value && requiresAuth) {
            // Clear Sentry user context when not authenticated
            sentryUtils.setUser(null)
            return { name: 'login' }
        }

        userStore.setUser(user.value)

        // Set Sentry user context when user is authenticated
        if (user.value) {
            sentryUtils.setUser({
                id: user.value.uuid,
                email: user.value.email || undefined,
                username: user.value.username || undefined,
            })
        }

        if (
            user.value &&
            (requiresGuest ||
                (requiresAuth &&
                    Array.isArray(user.value?.permissions) &&
                    Array.isArray(user.value?.roles) &&
                    typeof to.meta?.permission !== 'undefined' &&
                    !user.value?.permissions.map((p) => p.name).includes(to.meta?.permission) &&
                    !user.value?.roles.map((role) => role.name).includes('super-admin')))
        ) {
            return { name: 'dashboard' }
        }
    } catch (error) {
        if (requiresAuth) {
            return { name: 'login' }
        }
    }
})

export default router
